import {
    Box,
    Card as MuiCard,
    CardContent,
    Typography,
    Grid,
    Divider as MuiDivider,
    Button,
    withStyles,
    makeStyles,
    createStyles,
    Theme
} from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import { spacing } from "@material-ui/system";
import styled from "styled-components/macro";
import { AppStateType } from "../../../redux/reducers";
import { useDispatch, useSelector } from "react-redux";
import { setFacilities } from "../../../redux/actions/facilitiesActions";
import { useParams, useRouteMatch } from 'react-router';
import { partialUpdateProperty } from "../../../redux/actions/propertyActions";
import { getURL, urls, useRouting } from '../../../routes';
import { InfoCard } from '../../../components/SelectFacilitiesInfoCard';
import { SelectFacilities, UnitsType } from './SelectFacilities';
import { A11yStandardType } from '../../../models/utils';
import { Property } from '../../../models/Property';
import { useTranslation } from "react-i18next";
import i18n from "../../../translations/i18n";
import { fetchA11yStandard } from "../../../redux/actions/a11yStandardActions";

const Card = styled(MuiCard)(spacing);

const Divider = styled(MuiDivider)(spacing);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        facilitiesLabel: {
            width: "45%",
            [theme.breakpoints.down("xs")]: {
                width: "100%",
            },
        },
        marginLabel: {
            marginBottom: "18px",
        },
    }),
);


const StyledButton = withStyles({
    root: {
        border: '1px solid black',
        color: 'black',
        backgroundImage: 'linear-gradient(90deg, #84FAB0 0%, #8FEAFE 100%);',
        marginTop: "25px"
    },

})(Button);

export function getSteps(a11yStandard: A11yStandardType, property: Property, cacheUnits: UnitsType[] = []) {
    if (!a11yStandard) {
        return [];
    }
    const accessibility_unit_types = property?.accessibility_unit_types!
    const removeUnwantedLabels = (item: A11yStandardType["values"][number]) => {
        if (a11yStandard.key === 'cottage') return true;
        const keys = new Set([
            'bathroom__private',
            'toilet_room__private',
            'kitchen__private'
        ])
        return !keys.has(item.key)
    }
    return a11yStandard.values.filter(removeUnwantedLabels).map((item) => {
        let itemChecked: boolean = item.is_mandatory
        if (cacheUnits.length > 0) {
            const findCacheItem = cacheUnits.find((i) => i.key === item.key)
            if (findCacheItem) {
                itemChecked = findCacheItem.checked
            }
        }

        const unit: UnitsType = {
            label: item.title,
            checked: itemChecked,
            mandatory: item.is_mandatory,
            key: item.key
        }

        if (accessibility_unit_types?.length > 0) {
            if (accessibility_unit_types.includes(item.key)) {
                unit.checked = true
            }
        }
        return unit
    })
}



type FacilitiesFormProps = {
    onSave?: (steps?: string[]) => void
    facilities?: (steps?: string[] | undefined) => void | undefined
}

const standardTypeOptions: Record<string, string> = {
    hotel: "Hotel/Motel",
    office: "Office",
    shopping_mall: "Shopping mall",
    shop: "Shop"
}

export default function FacilitiesForm({ facilities }: FacilitiesFormProps) {
    const styles = useStyles();
    const dispatch = useDispatch()
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { url } = useRouteMatch();
    const { routeTo } = useRouting();
    const property = useSelector((state: AppStateType) => state.propertyReducer.property!)
    const user = useSelector((state: AppStateType) => state.authReducer.user!)
    const a11yStandardValues = useSelector((state: AppStateType) => state.a11yStandardReducer.a11yStandard)
    const a11yStandard: A11yStandardType = a11yStandardValues.find((item) => {
        return item.key === property.a11y_standard
    })
    const [cacheUnits, setCacheUnits] = useState<UnitsType[]>([])
    const [units, setUnits] = useState<UnitsType[]>(getSteps(a11yStandard, property, cacheUnits))

    useEffect(() => {
        setCacheUnits(units)
        if (a11yStandardValues.length === 0) {
            handlerA11yStandard()
        }
    }, [i18n.language])

    useEffect(() => {
        setUnits(getSteps(a11yStandard, property, cacheUnits))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [property, a11yStandard])

    useEffect(() => {
        handleSave(units)
        setUnits(units)
        console.log('units', units)
        if (facilities) {
            facilities(getCheckedKeyUnits())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [units])

    const mandatoryFacilities = units.filter((item) => item.mandatory)
    const optionalFacilities = units.filter((item) => !item.mandatory)

    const isValid = (keys: string[], accessibilityUnitTypes: string[] | undefined): boolean => {
        if (keys.length > 0 && accessibilityUnitTypes && accessibilityUnitTypes.length > 0) {
            return !keys.every((type) => accessibilityUnitTypes.includes(type))
        }
        return true
    }
    const onSave = (checkedKeyUnits: string[]) => {
        if (isValid(checkedKeyUnits, property.accessibility_unit_types)) {
            updatePropertyAccessibility(property.id, user.token, checkedKeyUnits)
        }
        routeTo(urls.editFacilities, { id: property.id })
    }
    const updatePropertyAccessibility = (id: string, token: string, steps: string[]) => {
        if (id) {
            const facilities = { "accessibility_unit_types": steps }
            dispatch(partialUpdateProperty(facilities, token, id))
        }
    }
    const handleSave = (units: UnitsType[]) => {
        let stepsLabel: string[] = []
        units.forEach((item) => {
            if (item.checked) {
                stepsLabel.push(item.key)
            }
        })
        dispatch(setFacilities(stepsLabel))
    }

    const getCheckedKeyUnits = (): string[] => {
        return units.filter(item => !item.mandatory && item.checked).map(i => i.key)
    }

    const handlerA11yStandard = async () => {
        await dispatch(async (dp: any) => {
            await fetchA11yStandard(user.token)(dp);
        })
    }

    return (
        <Box height="100%" width="100%">
            <Grid container>
                <Grid item xs>
                    <Box height='100%' py={4}>
                        <Grid container spacing={4}>
                            {
                                <>
                                    <Grid item xs={12} sm={12} md={8} lg>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    {t("facilitiesForm_selectFacilitiesForDataCollection")}
                                                </Typography>
                                                <Box mt={4}>
                                                    <Typography variant="h5" gutterBottom>
                                                        {t("facilitiesForm_mandatoryFacilitiesFor")} {standardTypeOptions[property ? property.a11y_standard! : '']} {t("facilitiesForm_standard")}
                                                    </Typography>
                                                    <Typography variant="body2" gutterBottom className={styles.marginLabel}>
                                                        {t("facilitiesForm_preselectedMandatoryItems")}
                                                    </Typography>
                                                    <Box mt={2} >
                                                        <SelectFacilities
                                                            units={mandatoryFacilities}
                                                            setUnits={setUnits}
                                                            initialUnits={units}
                                                        />
                                                    </Box>
                                                </Box>

                                                <Divider my={6} />
                                                <Box>
                                                    <Typography variant="h5" gutterBottom>
                                                        {t("facilitiesForm_optionalFacilitiesFor")} {standardTypeOptions[property ? property.a11y_standard! : '']} {t("facilitiesForm_standard")}
                                                    </Typography>
                                                    <Typography variant="body2" gutterBottom className={styles.marginLabel}>
                                                        {t("facilitiesForm_preselectedOptionalItems")}
                                                    </Typography>
                                                    <Box mt={2} >
                                                        <SelectFacilities
                                                            units={optionalFacilities}
                                                            setUnits={setUnits}
                                                            handleSave={handleSave}
                                                            initialUnits={units}
                                                        />
                                                    </Box>
                                                </Box>
                                                {url === getURL(urls.selectFacilities, { id: property ? property.id : id }) ? <Box>
                                                    <StyledButton
                                                        onClick={() => onSave(getCheckedKeyUnits())}
                                                    >
                                                        {t("generalWords_Next")}
                                                    </StyledButton>
                                                </Box> : null}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={4} lg={3}>
                                        <InfoCard />
                                    </Grid>
                                </>
                            }
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

